import './EventsDetail.scss'
import { useRef, useState } from 'react'
import { BLOCKS } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { css, cx, useTheme } from '@fable/theme'

import { renderParticipantsTag } from '../../utils'
import { getTicketPrice } from '../../utils'
import {
  renderVideo,
  descriptionStyles,
  isBeforeToday,
  getDateTime,
  getDateString,
} from '../../../../utils'
import { routes } from '../../routes'

import PlayIcon from '../../../../assets/icons/components/PlayIcon'
import ChevronIcon from '../../../../assets/icons/components/ChevronIcon'
import WatchIcon from '../../../../assets/icons/components/WatchIcon'
import InfoIcon from '../../../../assets/icons/components/InfoIcon'

import ShareButton from '../../../../components/share_button/ShareButton'
import { TITLES, RSVP_CTA_TEXT } from '../../../../constants'
import { FlexBox, Text } from '@fable/components'

const EventDetail = ({ event }) => {
  const { colors } = useTheme()
  const [isPlayingVideo, setIsPlayingVideo] = useState(false)
  const mediaNode = useRef()
  const isPastEvent = isBeforeToday(event?.date)

  const renderEventStatus = (date, ticketPrice) => {
    if (isPastEvent)
      return (
        <Text className="past-label" type="label" sizing="L">
          Past Event
        </Text>
      )

    const time = getDateString({
      date,
      pattern: 'h:mm a z',
    })

    return (
      <div className="event-labels above">
        <Text className="event-time" type="label" sizing="L">
          <WatchIcon />
          {time}
        </Text>
        <Text className="event-price" type="label" sizing="L">
          {getTicketPrice(ticketPrice)}
        </Text>
      </div>
    )
  }

  const renderMastheadButton = (slug, rsvpLink) => {
    let button
    if (isPastEvent) {
      button = (
        <ShareButton
          className={cx(
            css`
              margin-top: 40px;
            `
          )}
          category="event"
          title="Share"
          secondary
        />
      )
    } else {
      button = (
        <a
          className="rsvp-link masthead-button button solid"
          href={rsvpLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {RSVP_CTA_TEXT}
        </a>
      )
    }

    return button
  }

  const playVideo = () => {
    if (!isPlayingVideo) {
      setIsPlayingVideo(true)
      const iframe = mediaNode.current.children[0]
      iframe.src += '?autoplay=1'
    }
  }

  const renderMedia = (video, featuredImage) => {
    if (video) {
      const play = isPlayingVideo ? 'play' : ''

      return (
        <div
          className={`event-media video ${play}`}
          onClick={playVideo}
          ref={mediaNode}
        >
          {renderVideo(video)}
          <img src={featuredImage.url} alt={featuredImage.title}></img>
          <PlayIcon />
        </div>
      )
    }

    return (
      <div className="event-media">
        <img src={featuredImage.url} alt={featuredImage.title}></img>
      </div>
    )
  }

  const renderCallout = (callout) => {
    return (
      <div className="event-body callout">
        {documentToReactComponents(callout.json, descriptionStyles)}
      </div>
    )
  }

  const renderInfo = (info) => {
    return (
      <div className="event-body event-info">
        <Text className="info-label" type="label" sizing="L">
          <InfoIcon />
          About the event
        </Text>
        {documentToReactComponents(info.json, descriptionStyles)}
      </div>
    )
  }

  const renderParticipants = (participants) => {
    if (participants.length === 0) {
      return
    }

    const tag = renderParticipantsTag(participants)

    const participantBodyOptions = {
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => (
          <Text type="body" sizing="M">
            {children}
          </Text>
        ),
      },
    }

    const renderList = participants.map((participant, index) => {
      const { avatar, bio, name } = participant

      return (
        <div key={index} className="participant">
          <div className="participant-header">
            <img className="avatar" src={avatar.url} alt={avatar.title} />
            <Text type="body" sizing="M">
              {name}
            </Text>
          </div>
          <div className="participant-bio">
            {documentToReactComponents(bio.json, participantBodyOptions)}
          </div>
        </div>
      )
    })

    return (
      <>
        <div className="event-participants">
          {tag}
          <div className="participants-list">{renderList}</div>
        </div>
        <hr />
      </>
    )
  }

  const renderHost = (host) => {
    return (
      <div className="event-host">
        <FlexBox
          className={css`
            margin-bottom: 32px;
            text-transform: uppercase;
          `}
          alignItems="center"
        >
          <img
            className={cx(
              css`
                border-radius: 50%;
                border: 2px solid ${colors.whiteSmoke};
              `,
              'avatar'
            )}
            src={host.avatar.url}
            alt={host.avatar.title}
          />
          <Text type="label" sizing="L">
            Hosted by {host.name}
          </Text>
        </FlexBox>
        {documentToReactComponents(host.bio.json, descriptionStyles)}
      </div>
    )
  }

  const renderContent = () => {
    const {
      title,
      featuredImage,
      video,
      callout,
      info,
      date,
      ticketPrice,
      type,
      host,
      slug,
      rsvpLink,
      participantsCollection,
    } = event

    return (
      <>
        <section className="event-masthead wrap masthead light pad-edges">
          <Link className="back-button button mini solid" to={routes.events}>
            <ChevronIcon /> {TITLES.community.events}
          </Link>
          {renderEventStatus(date, ticketPrice)}
          <Text
            className={css`
              padding: 0 16px;
              max-width: 520px;
            `}
            type="header"
            sizing={{ base: 'M', mobileL: 'L', desktop: 'XL' }}
            text={title}
            align="center"
          />
          <div className="event-labels below">
            {type !== 'Panel' ? null : (
              <>
                <Text
                  className={cx(
                    css`
                      text-transform: uppercase;
                    `,
                    'event-type'
                  )}
                  type="label"
                  sizing="L"
                >
                  {type}
                </Text>
                <span
                  className={css`
                    margin: 0 4px;
                    position: relative;
                    top: -1px;
                    opacity: 0.2;
                  `}
                >
                  •
                </span>
              </>
            )}
            <Text
              className={cx(
                css`
                  text-transform: uppercase;
                `,
                'event-host'
              )}
              type="label"
              sizing="L"
            >
              with {host.name}
            </Text>
          </div>
          {renderMastheadButton(slug, rsvpLink)}
        </section>
        <section className="event-content-wrap wrap simple overlap">
          <div className="event-content content-wrap content-pad-edges overlap fade">
            <div className="event-media-wrap">
              {renderMedia(video, featuredImage)}
              {!isPastEvent && (
                <div className="event-date">
                  <Text className="date day" type="header" sizing="S" tag="h4">
                    {getDateTime(date).day}
                  </Text>
                  <Text className="date month" type="body" sizing="L">
                    {getDateTime(date).month}
                  </Text>
                </div>
              )}
            </div>
            <section className="slim">
              {renderCallout(callout)}
              {info && renderInfo(info)}
              <hr />
              {renderParticipants(participantsCollection.items)}
              {renderHost(host)}
              {!isPastEvent && (
                <a
                  className="rsvp-link button solid"
                  href={rsvpLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {RSVP_CTA_TEXT}
                </a>
              )}
            </section>
          </div>
        </section>
      </>
    )
  }

  return (
    <div className="events-detail screen-height">
      <div className="page-content fade">{renderContent()}</div>
    </div>
  )
}

EventDetail.propTypes = {
  location: PropTypes.object,
}

EventDetail.defaultProps = {
  location: {},
}

export default EventDetail
